import React, { useState, useEffect } from 'react';

/**
 * Typewriter effect for terminal text
 */
export const TerminalText = ({ children, delay = 0, speed = 30, onComplete, className = "" }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const text = String(children);

  useEffect(() => {
    setDisplayedText("");
    setIsComplete(false);
    
    const timeout = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < text.length) {
          setDisplayedText(text.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
          setIsComplete(true);
          onComplete?.();
        }
      }, speed);
      
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timeout);
  }, [text, delay, speed, onComplete]);

  return (
    <span className={className}>
      {displayedText}
      {!isComplete && <span className="cursor">▊</span>}
    </span>
  );
};

/**
 * Glitch text effect for cyberpunk aesthetic
 */
export const GlitchText = ({ children, intensity = "low" }) => {
  const [glitch, setGlitch] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < (intensity === "high" ? 0.3 : 0.1)) {
        setGlitch(true);
        setTimeout(() => setGlitch(false), 100);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [intensity]);

  return (
    <span className={`glitch-text ${glitch ? 'glitching' : ''}`} data-text={children}>
      {children}
    </span>
  );
};

/**
 * Terminal window wrapper component - NO macOS styling
 */
export const TerminalWindow = ({ title, children }) => {
  return (
    <div className="terminal-window">
      <div className="terminal-header">
        {/* NO DOTS - Just the title */}
        <span className="terminal-title">{title}</span>
      </div>
      <div className="terminal-body">
        {children}
      </div>
    </div>
  );
};

/**
 * Challenge type badge component
 */
export const TypeBadge = ({ type, config }) => {
  const { icon, label, color } = config[type] || config["coverage-gap"];
  
  return (
    <div className="type-badge" style={{ "--badge-color": color }}>
      <span className="badge-icon">{icon}</span>
      <span className="badge-label">{label}</span>
    </div>
  );
};

/**
 * Code block display component
 */
export const CodeBlock = ({ children, language = "java" }) => {
  return (
    <pre className="code-block">
      <code data-language={language}>{children}</code>
    </pre>
  );
};

/**
 * Test cases list display
 */
export const TestCasesList = ({ testCases }) => {
  return (
    <div className="test-cases">
      {testCases.map((tc, i) => (
        <div key={i} className="test-case">{tc}</div>
      ))}
    </div>
  );
};

/**
 * Requirements block display
 */
export const RequirementsBlock = ({ children }) => {
  return (
    <pre className="requirements-block">{children}</pre>
  );
};
